import { US_ZIP_REGEX } from '@federa/const/regexes';

/** The state-position scan's answer: the cursor after it, the state read (empty when none), and the zip carried through. */
type StatePartScan = { idx: number; state: string; zipCode: string };

/** ZIP sub-pattern (no anchors) for composition into larger regexes. */
const ZIP_BODY = US_ZIP_REGEX.source.slice(1, -1);
/** State + optional ZIP combiner, composed from the canonical US_ZIP_REGEX. */
const STATE_ZIP_REGEX = new RegExp(`^([A-Z]{2})(\\s+${ZIP_BODY})?$`);

function isCountryPart(part: string): boolean {
	return part === 'United States' || part === 'USA';
}

export function isZipCode(part: string): boolean {
	return US_ZIP_REGEX.test(part);
}

function extractStateAndZip(part: string): { state: string | undefined; zip: string } | null {
	const match = part.match(STATE_ZIP_REGEX);
	if (!match) return null;
	return { state: match[1], zip: match[2] ? match[2].trim() : '' };
}

function skipCountry(parts: string[], idx: number): number {
	return idx >= 0 && isCountryPart(parts[idx] ?? '') ? idx - 1 : idx;
}

function extractTrailingZip(parts: string[], idx: number): { idx: number; zipCode: string } {
	if (idx >= 0 && isZipCode(parts[idx] ?? '')) {
		return { idx: idx - 1, zipCode: parts[idx] ?? '' };
	}
	return { idx, zipCode: '' };
}

/** No state at this position -- the cursor does NOT advance and any zip found earlier passes through untouched. */
function noStatePart(idx: number, existingZip: string): StatePartScan {
	return { idx, state: '', zipCode: existingZip };
}

function extractStatePart(parts: string[], idx: number, existingZip: string): StatePartScan {
	if (idx < 0) return noStatePart(idx, existingZip);
	const stateInfo = extractStateAndZip(parts[idx] ?? '');
	if (!stateInfo) return noStatePart(idx, existingZip);
	const zip = stateInfo.zip && !existingZip ? stateInfo.zip : existingZip;
	return { idx: idx - 1, state: stateInfo.state ?? '', zipCode: zip };
}

export function parseAddressParts(parts: string[]): {
	street: string;
	city: string;
	state: string;
	zipCode: string;
} {
	let idx = skipCountry(parts, parts.length - 1);
	const zip = extractTrailingZip(parts, idx);
	idx = zip.idx;
	const st = extractStatePart(parts, idx, zip.zipCode);
	idx = st.idx;
	const city = idx >= 0 && st.state ? (parts[idx] ?? '') : '';
	if (city) idx--;
	const street = idx >= 0 ? parts.slice(0, idx + 1).join(', ') : '';
	return { street, city, state: st.state, zipCode: st.zipCode };
}
